import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import moment from "moment";
import emojiRegex from "emoji-regex";
import Animate from "components/animate/animate";
import PasswordField from "components/password-field";
import { AutoComplete_ZipCode } from "services/Profile";
import { useHistory } from "react-router-dom";
import { AuthConfigContext } from "context/AuthConfigProvider";
import Form from "@rjsf/core";
import ErrorTop from "components/error/error-top";
import { Register_Ecommerce_Member } from "services/Ecommerce";
import { ProfileDetailsContext } from "context/ProfileDetailsProvider";
import "./style.scss";
import validator from '@rjsf/validator-ajv8';

const formSchema = {
  "type": "object",
  "required": [
    "family_name",
    "first_name",
    "family_name_furigana",
    "name_furigana",
    "email",
    "password",
    "zipcode",
    "prefCode",
    "city",
    "street",
    "phone",
    "gender",
    "birthdate",
    "receive_dm",
    "receive_letter",
    "favorite_store",
    "agreement",
    "phone_permission",
    "email_permission"
  ],
  "properties": {
    "family_name": {
      "title": "姓",
      "type": "string",
      "minLength": 1,
      "maxLength": 45
    },
    "first_name": {
      "title": "名",
      "type": "string",
      "minLength": 1,
      "maxLength": 45
    },
    "family_name_furigana": {
      "title": "姓(カナ)",
      "type": "string",
      "minLength": 1,
      "maxLength": 45,
      "pattern": "^[ァ-ヶー]+$"
    },
    "name_furigana": {
      "title": "名(カナ)",
      "type": "string",
      "minLength": 1,
      "maxLength": 45,
      "pattern": "^[ァ-ヶー]+$"
    },
    "email": {
      "title": "メールアドレス",
      "type": "string",
      "format": "email",
      "maxLength": 45
    },
    "password": {
      "title": "パスワード",
      "type": "string",
      "minLength": 8,
      "maxLength": 16,
      "pattern": "^(?!.*[,:<>[\\]\"])[a-zA-Z0-9!@#$%^&*()_+{}|;'.?/~=`-]+$",
    },
    "zipcode": {
      "title": "郵便番号（ハイフン無し）",
      "type": "string",
      "format": "phone-jp"
    },
    "prefCode": {
      "title": "都道府県",
      "type": "string",
      "enum": [
        "0101", "0202", "0203", "0204", "0205", "0206", "0207",
        "0308", "0309", "0310", "0311", "0312", "0313", "0314",
        "0415", "0416", "0417", "0418", "0419", "0420", "0421",
        "0422", "0423", "0424", "0525", "0526", "0527", "0528",
        "0529", "0530", "0631", "0632", "0633", "0634", "0635",
        "0736", "0737", "0738", "0739", "0840", "0841", "0842",
        "0843", "0844", "0845", "0846", "0847"
      ],
      "enumNames": [
        "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
        "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
        "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
        "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
        "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
        "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
        "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
      ]
    },
    "city": {
      "title": "住所１（市区町村）",
      "type": "string",
      "minLength": 2,
      "maxLength": 45
    },
    "street": {
      "title": "住所２（番地）",
      "type": "string",
      "minLength": 1,
      "maxLength": 45
    },
    "building": {
      "title": "住所３（建物名・部屋番号）",
      "type": "string",
      "maxLength": 45
    },
    "phone": {
      "title": "電話番号（ハイフン無し）",
      "type": "string",
      "format": "phone-jp",
      "maxLength": 11,
      "minLength": 10
    },
    "phone_permission": {
      "title": "電話可否",
      "type": "string",
      "enum": ["可", "不可", "選択なし"],
      "enumNames": ["可", "不可", "選択なし"],
      "default": "可"
    },
    "email_permission": {
      "title": "メール可否",
      "type": "string",
      "enum": ["可", "不可", "選択なし"],
      "enumNames": ["可", "不可", "選択なし"],
      "default": "可"
    },
    "gender": {
      "title": "性別",
      "type": ["string", "null"],
      "disabled": false,
      "anyOf": [
        {
          "type": "string",
          "title": "男性",
          "enum": ["male"]
        },
        {
          "type": "string",
          "title": "女性",
          "enum": ["female"]
        },
        {
          "type": "string",
          "title": "選択なし",
          "enum": ["other"]
        }
      ]
    },
    "birthdate": {
      "title": "生年月日",
      "type": "string",
      "pattern": "^[0-9]{8}$",
      "minLength": 8,
      "maxLength": 8
    },
    "receive_dm": {
      "title": "店舗からの特別なイベント・セールのご案内",
      "type": "boolean",
      "default": true,
      "oneOf": [
        {
          "title": "受取る",
          "const": true
        },
        {
          "title": "受取らない",
          "const": false
        }
      ]
    },
    "receive_letter": {
      "title": "メールマガジン",
      "type": "boolean",
      "default": true,
      "oneOf": [
        {
          "title": "受取る",
          "const": true
        },
        {
          "title": "受取らない",
          "const": false
        }
      ]
    },
    "favorite_store": {
      "title": "お気に入り店舗",
      "type": "string",
      "enum": [
        "31901", "30309", "31702", "40405", "30809", "31801", "30308",
        "31005", "40305", "41201", "31501", "31015", "31013",
        "31102", "40203", "30702", "31011", "40205", "31002",
        "30901", "40103", "40102", "40403", "40302", "41301",
        "40101", "40401", "40601", "31004", "30804", "31109",
        "30805", "31012", "40801", "40202", "40404", "31007",
        "31001", "40110", "31016", "31101", "31017", "40301",
        "40111", "40307", "30306", "40402", "40107", "31014",
        "41401", "40304", "40306", "30304", "30507", "30509",
        "31018", "30504", "30501", "20200", "30802", "30301",
        "40303", "30508", "30502", "30808", "30206", "30807",
        "30302", "30505", "30803", "30506", "30202", "30307",
        "30204", "30801", "31701", "31110", "30401", "30305"
      ],
      "enumNames": [
        "NEWoMan横浜店", "イオンモールKAGOSHIMA-BAY店", "サンエー西原シティ店", "JR名古屋高島屋店",
        "天王寺ミオ店", "ニューポートひたちなかファッションクルーズ店", "イオンモール須坂店",
        "三井ｱｳﾄﾚｯﾄﾊﾟｰｸ札幌北広島店", "大丸札幌店", "FKDインターパーク店",
        "高崎オーパ店", "三井OP入間店", "ららぽーと新三郷店",
        "浦和パルコ店", "そごう大宮店", "モラージュ菖蒲店",
        "ららぽーと富士見店", "そごう千葉店", "ららぽｰとTOKYO-BAY店",
        "流山おおたかの森S.C.店", "銀座三越店", "日本橋三越本店",
        "日本橋タカシマヤ店", "大丸東京店", "東武百貨店池袋店",
        "伊勢丹新宿店", "新宿高島屋店", "グランデュオ立川店",
        "三井ｱｳﾄﾚｯﾄﾊﾟｰｸ多摩南大沢店", "セレオ国分寺店", "錦糸町パルコ店",
        "セレオ八王子店", "ららぽーと海老名店", "東急百貨店たまプラーザ店",
        "そごう横浜店", "横浜高島屋店", "ラゾーナ川崎プラザ店",
        "ららぽｰと横浜店", "新潟伊勢丹店", "ららぽーと沼津店",
        "静岡パルコ店", "ららぽーと愛知東郷店", "松坂屋名古屋店",
        "JR京都伊勢丹店", "大丸京都店", "イオンモール橿原店",
        "大阪高島屋店", "ルクア1100店", "ららぽーとEXPOCITY店",
        "京阪モール店", "大丸梅田店", "大丸神戸店",
        "イオンモール岡山店", "ゆめタウン広島店", "ゆめタウン高松店",
        "ららぽｰと福岡店", "ゆめタウン久留米店", "ゆめタウン行橋店",
        "天神本店", "アミュプラザ小倉店", "イオンモール福岡店",
        "大丸福岡天神店", "ゆめタウン佐賀店", "夢彩都店",
        "アミュプラザ長崎店", "イオン大村店", "アミュプラザ熊本店",
        "イオンモール熊本店", "ゆめタウン光の森店", "アミュプラザおおいた店",
        "ゆめタウン別府店", "パークプレイス大分店", "イオンモール宮崎店",
        "イオン都城店", "アミュプラザ鹿児島店", "サンエー那覇メインプレイス店",
        "サンエーパルコ沖縄浦添店", "イオン那覇店", "イオンモール沖縄ライカム店"
      ]
    },
    "marital_status": {
      "title": "未婚/既婚",
      "type": "string",
      "default": "選択なし",
      "enum": [
        "未婚",
        "既婚",
        "選択なし"
      ]
    },
    "anniversary": {
      "title": "結婚記念日",
      "type": "string",
      "format": "date"
    },
    "occupation": {
      "title": "職業",
      "type": "string",
      "default": "",
      "enum": [
        "",
        "会社員",
        "公務員",
        "自営業/個人事業",
        "会社役員",
        "自由業",
        "専業主婦・主夫",
        "学生",
        "アルバイト/パート",
        "無職",
        "その他"
      ]
    },
    "agreement": {
      "type": "boolean",
      "title": "利用規約に同意の上、保存してください。",
      "enum": [true],
      "default": true
    }
  }
}

const uiSchemaEcommerce = {
  "ui:order": [
    "family_name",
    "first_name",
    "family_name_furigana",
    "name_furigana",
    "email",
    "password",
    "phone",
    "phone_permission",
    "email_permission",
    "gender",
    "birthdate",
    "zipcode",
    "prefCode",
    "city",
    "street",
    "building",
    "receive_letter",
    "receive_dm",
    "favorite_store",
    "marital_status",
    "anniversary",
    "occupation",
    "agreement"
  ],
  "family_name": {
    "ui:widget": "CustomTextWidget"
  },
  "first_name": {
    "ui:widget": "CustomTextWidget"
  },
  "family_name_furigana": {
    "ui:widget": "CustomTextWidget"
  },
  "name_furigana": {
    "ui:widget": "CustomTextWidget"
  },
  "email": {
    "ui:placeholder": "example@gmail.com"
  },
  "password": {
    "ui:field": "PasswordField",
    "ui:help": (
      <div style={{ fontSize: '12px' }}>
        <>
          <span>
            ※半角英数字記号の8～16文字で入力してください。
          </span>
          <br />
          <span>
            ※「”」「,」「:」「&lt;」「&gt;」「[」「]」は利用できません。
          </span>
        </>
      </div>
    )
  },
  "phone_permission": {
    "ui:widget": "radio"
  },
  "email_permission": {
    "ui:widget": "radio"
  },
  "city": {
    "ui:placeholder": "品川区西五反田"
  },
  "street": {
    "ui:placeholder": "7-20-9"
  },
  "building": {
    "ui:placeholder": ""
  },
  "birthdate": {
    "ui:widget": "CustomBirthdateWidget"
  },
  "gender": {
    "ui:widget": "radio"
  },
  "receive_dm": {
    "ui:widget": "radio"
  },
  "receive_letter": {
    "ui:widget": "radio"
  },
  "agreement": {
    "ui:help": (
      <div>
        <>
          <a target="_blank" rel="noopener noreferrer" href="/agreement">
            利用規約はこちら
          </a>
        </>
      </div>
    )
  },
  "anniversary": {
    "ui:help": (
      <div>
        <>
          <button className="form-control" type="button" style={{ width: '180px', marginTop: '10px' }} onClick={() => resetAnniversaryValue()}>結婚記念日リセット</button>
        </>
      </div>
    )
  }
}

const resetAnniversaryValue = () => {
  if (document.getElementById('root_anniversary')) {
    document.querySelector('#root_anniversary').value = ""
  }
};

// CustomTextWidgetをコンポーネント外に定義（再レンダリングによるフォーカス喪失を防止）
const CustomTextWidget = (props) => {
  const { value, onChange, options, ...rest } = props;

  const handleChange = (event) => {
    const inputValue = event.target.value;
    onChange(inputValue || undefined);
  };

  return (
    <div>
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        className={`custom-input-field`}
        {...rest}
      />
    </div>
  );
};

// CustomDateWidgetをコンポーネント外に定義（再レンダリングによるフォーカス喪失を防止）
const CustomDateWidget = ({
  value,
  onChange,
  readonly,
  disabled,
  options,
}) => {
  if (readonly && value) {
    return (
      <input
        type="text"
        value={value || ""}
        readOnly={readonly}
        disabled={disabled}
        className="form-control readonly-date-field"
        onClick={(e) => {
          if (readonly) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      />
    );
  }

  return (
    <input
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      readOnly={readonly}
      disabled={disabled}
      className="form-control"
    />
  );
};

const CreateAccount = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const authConf = useContext(AuthConfigContext);
  const [schema, setSchema] = useState({});
  const [profileDetails] = useContext(ProfileDetailsContext);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uiSchema, setUiSchema] = useState({});
  const [loading, setLoading] = useState(false);
  // フォームが初期化済みかどうか（入力中のリセット防止）
  const formInitializedRef = React.useRef(false);

  useEffect(() => {
    const initializeSchema = () => {
      setSchema(formSchema);
      setUiSchema(uiSchemaEcommerce);
    };
    initializeSchema();
  }, [authConf]);

  useEffect(() => {
    const populateFormData = () => {
      const profileIsEmpty = Object.keys(profileDetails).length === 0;
      // 既にフォームが初期化済みの場合はスキップ（入力中のリセット防止）
      if (formInitializedRef.current) {
        return;
      }
      if (!profileIsEmpty) {
        let convertedBirthDay = undefined;
        if (profileDetails.birthdate) {
          convertedBirthDay = createBirthDateString(profileDetails.birthdate)
        }
        let matchedFormData = {
          family_name: profileDetails.family_name,
          first_name: profileDetails.first_name,
          family_name_furigana: profileDetails.family_name_furigana,
          name_furigana: profileDetails.name_furigana,
          email: profileDetails.email,
          zipcode: profileDetails.custom_data?.zipcode,
          prefCode: profileDetails.custom_data?.prefCode,
          city: profileDetails.custom_data?.city,
          street: profileDetails.custom_data?.street,
          building: profileDetails.custom_data?.building,
          phone: profileDetails.phone,
          gender: profileDetails.gender,
          birthdate: convertedBirthDay,
          receive_letter: profileDetails.custom_data?.receive_letter,
          receive_dm: profileDetails.custom_data?.receive_dm,
          favorite_store: profileDetails.custom_data?.favorite_store,
          marital_status: profileDetails.custom_data?.marital_status,
          anniversary: profileDetails.custom_data?.anniversary,
          occupation: profileDetails.custom_data?.occupation,
          agreement: profileDetails.custom_data?.agreement,
          phone_permission: profileDetails.phone_permission,
          email_permission: profileDetails.email_permission,
        }
        if (matchedFormData.receive_letter == null) {
          matchedFormData.receive_letter = true;
        }
        if (matchedFormData.receive_dm == null) {
          matchedFormData.receive_dm = true;
        }
        if (!['未婚', '既婚', '選択なし'].includes(matchedFormData.marital_status)) {
          matchedFormData.marital_status = '選択なし';
        }
        if (!profileDetails.first_name && profileDetails.identities && !profileDetails.identities.filter((i) => i.provider === "festaria").length) {
          matchedFormData.first_name = ""
        }
        setFormData(matchedFormData)
        // フォーム初期化完了をマーク
        formInitializedRef.current = true;
      }
    };
    populateFormData();
  }, [schema, profileDetails])


  const createBirthDateString = (day) => {
    let bdate = new Date(day)
    const offset = bdate.getTimezoneOffset();
    bdate = new Date(bdate.getTime() - (offset * 60 * 1000));
    return bdate.toISOString().split('T')[0].replaceAll("-", "");
  }

  const submit = async (values) => {
    let dataVals = values.formData;

    setFormData(dataVals);

    dataVals.client_id = authConf?.configJson?.client_id

    // 住所関連フィールドはDOMの値を優先的に取得（ブラウザオートコンプリート対策）
    // React stateとDOMの値が不整合の場合、DOMに表示されている値を信頼する
    const zipcodeInput = document.getElementById('root_zipcode');
    const prefCodeInput = document.getElementById('root_prefCode');
    const cityInput = document.getElementById('root_city');
    const streetInput = document.getElementById('root_street');
    const buildingInput = document.getElementById('root_building');

    if (zipcodeInput && zipcodeInput.value) {
      dataVals.zipcode = zipcodeInput.value.toString().replaceAll(/[^\d]/g, '');
    } else if (dataVals?.zipcode) {
      dataVals.zipcode = dataVals.zipcode.toString()
    } else if (dataVals?.zip_code) {
      dataVals.zipcode = dataVals.zip_code.toString()
    }

    if (prefCodeInput && prefCodeInput.value) {
      dataVals.prefCode = prefCodeInput.value;
    }

    if (cityInput && cityInput.value) {
      dataVals.city = cityInput.value;
    }

    if (streetInput && streetInput.value) {
      dataVals.street = streetInput.value;
    }

    if (buildingInput && buildingInput.value) {
      dataVals.building = buildingInput.value;
    }

    if (document.getElementById('root_anniversary')) {
      if (document.querySelector('#root_anniversary').value === "") {
        dataVals.anniversary = ""
      }
    }
    // console.log(dataVals)
    // return
    setLoading(true)
    const token = localStorage.getItem("accesstoken") || "";
    const dataPost = await Register_Ecommerce_Member(dataVals, token);
    // console.log('dataPost res:', dataPost);
    if (dataPost?.message === 'Success') {
      // console.log('dataPost success:', dataPost);
      Swal.fire({
        icon: "success",
        title: t("profile_page.create_page.create_success"),
        showConfirmButton: false,
        allowOutsideClick: false,
      });
      setTimeout(() => {
        setLoading(false)
        Swal.close();
        window.location.href = "/home";
      }, 1500);
    } else {
      setLoading(false)
      console.log('error in registerUser:', dataPost)
      setError(true);
      let errorDescription = dataPost?.response?.data?.errors?.error_description || dataPost?.response?.data?.message
      if (errorDescription) {
        setErrorMessage(errorDescription);
      } else {
        setErrorMessage("システムエラーが発生しました、しばらくお待ちください。")
      }
    }
  }

  // useEffect(() => {
  //   if (profileDetails.gender && profileDetails.ec_id) {
  //     window.location.href = '/home';
  //   }
  // }, [profileDetails])

  useEffect(() => {
    // Reset error message after 7 seconds
    setTimeout(() => {
      setError(false)
      setErrorMessage('')
    }, 7000);
  }, [error, errorMessage])

  const customFormats = {
    'phone-jp': /^[0-9]*$/
  };

  // 絵文字が含まれているかをチェック（emoji-regexライブラリ使用 - Unicode標準準拠）
  const containsEmoji = (value) => {
    if (typeof value !== 'string') return false;
    const regex = emojiRegex();
    return regex.test(value);
  };

  // 絵文字エラーがあるフィールドを追跡（transformErrorsでパターンエラーを抑制するため）
  // useRefを使用してState更新による再レンダリングを防ぐ
  const emojiErrorFieldsRef = React.useRef(new Set());

  // Custom validation function for React JSON Schema Form
  const customValidate = (formData, errors) => {
    // Validate birthdate (mandatory field)
    const birthdateError = validateDate(formData.birthdate);
    if (birthdateError) {
      errors.birthdate.addError(birthdateError);
    }

    // 絵文字バリデーション（姓・名・カタカナフィールド）
    // 絵文字エラーを優先し、パターンエラーは表示しない
    emojiErrorFieldsRef.current = new Set();

    if (formData.family_name && containsEmoji(formData.family_name)) {
      errors.family_name.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.family_name');
    }
    if (formData.first_name && containsEmoji(formData.first_name)) {
      errors.first_name.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.first_name');
    }
    if (formData.family_name_furigana && containsEmoji(formData.family_name_furigana)) {
      errors.family_name_furigana.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.family_name_furigana');
    }
    if (formData.name_furigana && containsEmoji(formData.name_furigana)) {
      errors.name_furigana.addError('使用できない文字（絵文字・記号など）が含まれています。');
      emojiErrorFieldsRef.current.add('.name_furigana');
    }

    return errors;
  };

  // Custom date validation function using Moment.js
  const validateDate = (dateString) => {
    // 空チェックはスキーマのrequiredバリデーションに任せる
    if (!dateString || dateString.trim() === '') {
      return null;
    }

    // Check if date format is exactly YYYYMMDD (8 digits)
    const dateRegex = /^\d{8}$/;
    if (!dateRegex.test(dateString)) {
      return "日付はYYYYMMDD形式（8桁の数字）で入力してください。";
    }

    // Parse YYYYMMDD format using Moment.js
    const momentDate = moment(dateString, 'YYYYMMDD', true); // strict parsing

    // Check if date is valid
    if (!momentDate.isValid()) {
      return "有効な日付を入力してください。";
    }

    // Check if date is in the future
    if (momentDate.isAfter(moment(), 'day')) {
      return "未来の日付は入力できません。";
    }

    // Check if date is too far in the past (reasonable age limit - 150 years)
    const minDate = moment().subtract(150, 'years');
    if (momentDate.isBefore(minDate, 'day')) {
      return "有効な生年月日を入力してください。";
    }

    return null; // Valid date
  };

  function transformErrors(errors) {

    var errorMessage = [];
    var existErrors = [];

    // if(document.getElementById('root_family_name')){
    //    var root_family_name =  document.querySelector('#root_family_name').value;
    //    if(root_family_name.trim().length === 0){
    //       var name_error = {
    //         "name": "required",
    //         "property": ".family_name",
    //         "message": "必須項目を入力してください。",
    //         "params": {
    //             "missingProperty": "family_name"
    //         },
    //         "stack": ".family_name is a required property",
    //         "schemaPath": "#/required"
    //       }
    //
    //       errors.push(name_error)
    //    }
    // }
    if (document.getElementById('root_zipcode')) {
      var root_zipcode = document.querySelector('#root_zipcode').value;
      if (root_zipcode.trim().length !== 7) {
        var zipcode_error = {
          "name": "format",
          "property": ".zipcode",
          "message": "7桁で半角数字のみを入力してください。",
          "params": {
            "format": "phone-jp"
          },
          "stack": ".zipcode should match format \"phone-jp\"",
          "schemaPath": "#/properties/zipcode/format"
        }

        errors.push(zipcode_error)
      }
    }
    if (document.getElementById('root_email')) {
      var root_email = document.querySelector('#root_email').value;
      const emailRegex = /\.(cn|us|uk|de|fr|in|ca|au|br|ru|jp|kr|it|es|nl|se|mx|ch|ar|com|org|net|edu|gov|info|biz|name|pro|tv|int|xyz|shop|tech|app|blog|news|music|store|club|co|online|me|design|mil|coop|aero|museum|asia|eu|lat|africa|sg|team|tw|io|vn|re|cloud|md|ph)$/;
      const isValidEmail = emailRegex.test(root_email);
      if (!isValidEmail) {
        var email_error = {
          "name": "format",
          "property": ".email",
          "message": "有効なメールアドレスを入力してください。",
          "params": {
            "format": "email"
          },
          "stack": ".email should match format email",
          "schemaPath": "#/properties/email/format"
        }

        errors.push(email_error)
      }
    }
    console.log(errors.filter(error => error.property.includes("name")))
    errors.some(error => {
      if (error.property === ".occupation" || error.property === ".anniversary") {
        return false;
      }
      if (error.name === "enum") {
        if (error.property === ".agreement") {
          error.message = "利用規約の同意が必要になります"
          errorMessage.push(error)
        } else if (error.property === ".gender") {
          if (existErrors.includes(".gender")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else if (error.property === ".favorite_store") {
          if (existErrors.includes(".favorite_store")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else if (error.property === ".prefCode") {
          if (existErrors.includes(".prefCode")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else {
          error.message = "必須項目を入力してください。";
          errorMessage.push(error)
        }
      }
      // if(error.property === ".family_name" || error.property === ".email") {
      //   error.message= "必須項目を入力してください。"
      //   errorMessage.push(error)
      // }
      if (error.name === "required" && error.property !== '.zipcode') {
        error.message = "必須項目を入力してください。";
        errorMessage.push(error)
        existErrors.push(error.property)
      }
      if (error.name === "type") {
          if (error.property === '.favorite_store' || error.property === '.prefCode' || error.property === '.gender' || error.property === '.zipcode' || error.property === '.email') {
          return false;
        }
        if (error.params.type === "string" && !existErrors.includes(error.property)) {
          error.message = "必須項目を入力してください。";
          errorMessage.push(error)
        }
      }
      if (error.name === "maxLength") {
        if (error.property === ".password") {
          if (existErrors.includes(".password")) {
            return false;
          }
          existErrors.push(error.property)
          // error.message = `8文字以上${error.params.limit || 16}文字以下を入力してください`
          // error.message = '半角英数字記号の8～16文字で入力してください。'
          error.message = '8～16文字で半角英数字記号のみを入力してください。「"」「,」「:」「<」「>」「[」「]」は利用できません。'
          errorMessage.push(error)
        } else if (error.property === ".phone") {
          if (existErrors.includes(".phone")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '10〜11桁で半角数字のみを入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".email") {
          if (existErrors.includes(".email")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '45文字以内で半角英数記号のみを入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".city") {
          if (existErrors.includes(".city")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '2〜45文字で入力してください。'
          errorMessage.push(error)
        } else {
          error.message = `${error.params.limit}文字以内で入力してください。`
          errorMessage.push(error)
        }
      }
      if (error.name === "minLength" || error.name === "pattern") {
        if (error.property === ".family_name_furigana" && error.name === "pattern") {
          // 絵文字エラーがある場合はパターンエラーをスキップ（絵文字エラーを優先）
          // DOMから直接値を取得して絵文字チェック（customValidateのエラーはtransformErrorsに渡されないため）
          var familyNameFuriganaInput = document.getElementById('root_family_name_furigana');
          var familyNameFuriganaValue = familyNameFuriganaInput ? familyNameFuriganaInput.value : '';
          if (existErrors.includes(".family_name_furigana") || containsEmoji(familyNameFuriganaValue)) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '全角カタカナのみで入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".name_furigana" && error.name === "pattern") {
          // 絵文字エラーがある場合はパターンエラーをスキップ（絵文字エラーを優先）
          // DOMから直接値を取得して絵文字チェック（customValidateのエラーはtransformErrorsに渡されないため）
          var nameFuriganaInput = document.getElementById('root_name_furigana');
          var nameFuriganaValue = nameFuriganaInput ? nameFuriganaInput.value : '';
          if (existErrors.includes(".name_furigana") || containsEmoji(nameFuriganaValue)) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '全角カタカナのみで入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".password") {
          if (existErrors.includes(".password")) {
            return false;
          }
          existErrors.push(error.property)
          // error.message = `半角英数字${error.params.limit || 8}文字以上で入力してください`
          error.message = '8～16文字で半角英数字記号のみを入力してください。「"」「,」「:」「<」「>」「[」「]」「`」は利用できません。'
          errorMessage.push(error)
        } else if (error.property === ".phone") {
          if (existErrors.includes(".phone")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '10〜11桁で半角数字のみを入力してください。'
          errorMessage.push(error)
        } else if (error.property === ".birthdate") {
          if (existErrors.includes(".birthdate")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '8桁で半角数字のみを入力してください（例: 19900101）。'
          errorMessage.push(error)
        } else if (error.property === ".city") {
          if (existErrors.includes(".city")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = '2〜45文字で入力してください。'
          errorMessage.push(error)
        } else if (error.name === "minLength" && error.params.limit === 1) {
          // 名前フィールドのminLength: 1は必須項目として扱う
          error.message = "必須項目を入力してください。"
          errorMessage.push(error)
        } else {
          error.message = error.params.limit ? `${error.params.limit}文字以下ではありません` : `入力形式が正しくありません`
          errorMessage.push(error)
        }
      }
      if (error.name === "format") {
        if (error.params.format === "phone-jp" && error.property !== ".zipcode") {
          if (existErrors.includes(".phone")) {
            return false;
          }
          existErrors.push(error.property)
          error.message = "10〜11桁で半角数字のみを入力してください。"
          errorMessage.push(error)
        } else if (error.params.format === "email") {
          console.log(`existErrors`, existErrors)
          if (existErrors.includes(".email")) {
            return false;
          }
          existErrors.push(".email")
          error.message = "有効なメールアドレスを入力してください。"
          errorMessage.push(error)
        }
      }
      if (error.property === ".zipcode") {
        if (existErrors.includes(".zipcode")) {
          return false;
        }
        existErrors.push(error.property)
        error.message = `7桁で半角数字のみを入力してください。`
        errorMessage.push(error)
      }
      // if(error.message === "should match some schema in anyOf") {
      //   if(error.property === ".gender") {
      //     error.message = "必須項目を入力してください。"
      //     errorMessage.push(error)
      //   } else {
      //     error.message = "";
      //   }
      // } 
      if (error.message === "should be boolean") {
        error.message = "";
      }
      // 絵文字エラーは既に先頭で処理済みなのでスキップ
      if (error.message === '使用できない文字（絵文字・記号など）が含まれています。') {
        return false;
      }
      // return error;
      return false;
    });

    return errorMessage;

  };

  function onError(errors) {
    const errorField = document.querySelector('.field-error');
    if (errorField) errorField.scrollIntoView();
  }

  // Custom birthdate widget with 8-digit limit - memoized to prevent re-creation
  // LINE IAB対応: type="tel" + inputMode="numeric" + pattern="[0-9]*" で数字キーパッドを強制表示
  const CustomBirthdateWidget = React.useCallback(({
    value,
    onChange,
    disabled,
  }) => {
    const handleChange = (e) => {
      const inputValue = e.target.value;
      // 数字のみフィルタリング（8文字制限はmaxLength属性に委譲）
      const digitsOnly = inputValue.replace(/[^0-9]/g, '');
      onChange(digitsOnly);
    };

    return (
      <input
        type="tel"
        value={value || ""}
        onChange={handleChange}
        disabled={disabled}
        className="form-control"
        placeholder="YYYYMMDD"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={8}
      />
    );
  }, []);

  return (
    <Animate>
      <div className="profile-create">
        <div className="card">
          {error && <ErrorTop message={errorMessage} />}
          <div className="card-title">
            <h1>{t("profile_page.create_page.title")}</h1>
            <div className="title-bar"></div>
          </div>
          <div className="card-body">
            {schema && Object.keys(schema).length > 0 ? (
              <Form
                noHtml5Validate
                validate={customValidate}
                transformErrors={transformErrors}
                onError={onError}
                customFormats={customFormats}
                schema={schema}
                uiSchema={uiSchema}
                fields={{ PasswordField }}
                formData={formData}
                onSubmit={values => submit(values)}
                onChange={(form) => AutoComplete_ZipCode(form, setFormData)}
                validator={validator}
                widgets={{
                  CustomTextWidget: CustomTextWidget,
                  CustomBirthdateWidget: CustomBirthdateWidget,
                  CustomDateWidget: CustomDateWidget // 关键：这个名字要跟 JSON 里的一样
                }}
              >
                <div className="action-area">
                  <div className="back">
                    <button type="button" className="btn btn-back" onClick={() => history.goBack()}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </div>
                  <div className="submission">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-save"
                    >
                      {t("profile_page.edit_page.button_submit")}
                    </button>
                  </div>
                </div>
              </Form>
            ) : (
              <>
                <div className="no-schema">
                  <span>{t("common.no_info")}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Animate>
  );
};

export default CreateAccount;
